import React, { useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/it';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';

// Setup moment localizer con locale italiano
moment.locale('it');
const localizer = momentLocalizer(moment);

const CalendarView = ({ bookings }) => {
  // Trasforma i bookings in eventi per il calendario
  const events = useMemo(() => {
    return bookings.map((booking) => {
      const [hours, minutes] = booking.preferred_time.split(':');
      const startDate = new Date(booking.preferred_date);
      startDate.setHours(parseInt(hours), parseInt(minutes), 0);
      
      // Durata appuntamento: 30 minuti
      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + 30);
      
      return {
        id: booking.id || Math.random().toString(),
        title: `${booking.parent_name}`,
        start: startDate,
        end: endDate,
        resource: booking,
        status: booking.status || 'pending'
      };
    });
  }, [bookings]);

  // Personalizzazione colori eventi in base allo status
  const eventStyleGetter = (event) => {
    let backgroundColor = '#E91E8C'; // Default pink
    
    if (event.status === 'confirmed') {
      backgroundColor = '#10b981'; // Green
    } else if (event.status === 'cancelled') {
      backgroundColor = '#ef4444'; // Red
    } else if (event.status === 'pending') {
      backgroundColor = '#f59e0b'; // Orange
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontWeight: '500',
        fontSize: '13px',
        padding: '4px 8px'
      }
    };
  };

  // Componente personalizzato per mostrare evento nel calendario
  const EventComponent = ({ event }) => (
    <div className="flex items-center justify-between gap-1">
      <span className="truncate">{event.title}</span>
      <span className="text-xs opacity-90">{moment(event.start).format('HH:mm')}</span>
    </div>
  );

  // Dettagli evento al click
  const handleSelectEvent = (event) => {
    const booking = event.resource;
    const formattedDate = moment(event.start).format('dddd D MMMM YYYY');
    const formattedTime = moment(event.start).format('HH:mm');
    
    alert(
      `📅 PRENOTAZIONE\n\n` +
      `Nome: ${booking.parent_name}\n` +
      `Email: ${booking.email}\n` +
      `Telefono: ${booking.phone}\n` +
      `Data: ${formattedDate}\n` +
      `Ora: ${formattedTime}\n` +
      `Età bambino: ${booking.child_age || 'N/A'}\n` +
      `Messaggio: ${booking.message || 'Nessun messaggio'}\n` +
      `Status: ${booking.status || 'pending'}`
    );
  };

  // Messaggi calendario in italiano
  const messages = {
    allDay: 'Tutto il giorno',
    previous: 'Indietro',
    next: 'Avanti',
    today: 'Oggi',
    month: 'Mese',
    week: 'Settimana',
    day: 'Giorno',
    agenda: 'Agenda',
    date: 'Data',
    time: 'Ora',
    event: 'Evento',
    noEventsInRange: 'Nessuna prenotazione in questo periodo',
    showMore: (total) => `+ altri ${total}`
  };

  return (
    <div className="space-y-6">
      {/* Legenda colori */}
      <Card className="border-0 shadow-md bg-gradient-to-r from-pink-50 to-purple-50">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-sm font-semibold text-gray-700">Legenda:</span>
            <div className="flex items-center gap-2">
              <Badge className="bg-[#f59e0b] hover:bg-[#f59e0b]">Pending</Badge>
              <span className="text-sm text-gray-600">In attesa</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-[#10b981] hover:bg-[#10b981]">Confirmed</Badge>
              <span className="text-sm text-gray-600">Confermata</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-[#ef4444] hover:bg-[#ef4444]">Cancelled</Badge>
              <span className="text-sm text-gray-600">Cancellata</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendario */}
      <Card className="border-0 shadow-xl">
        <CardContent className="p-6">
          <div style={{ height: '700px' }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              eventPropGetter={eventStyleGetter}
              components={{
                event: EventComponent
              }}
              onSelectEvent={handleSelectEvent}
              messages={messages}
              views={['month', 'week', 'day', 'agenda']}
              defaultView="month"
              popup
              selectable
              tooltipAccessor={(event) => `${event.title} - ${moment(event.start).format('HH:mm')}`}
            />
          </div>
        </CardContent>
      </Card>

      {/* Statistiche rapide */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-md bg-orange-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-orange-600">
              {bookings.filter(b => b.status === 'pending' || !b.status).length}
            </p>
            <p className="text-sm text-gray-600 mt-1">In attesa</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-green-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-600">
              {bookings.filter(b => b.status === 'confirmed').length}
            </p>
            <p className="text-sm text-gray-600 mt-1">Confermate</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-md bg-red-50">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-600">
              {bookings.filter(b => b.status === 'cancelled').length}
            </p>
            <p className="text-sm text-gray-600 mt-1">Cancellate</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CalendarView;
